import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AuthContext, API } from '../App';
import {
  Copy, Check, Loader2, Lock, Sparkles, ArrowLeft, Clock,
  FileText, Mail, MessageSquare, CheckSquare, Briefcase, Building2, Square, Download
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PackageBanner } from '../components/PackageBanner';
import axios from 'axios';
import { toast } from 'sonner';

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const go = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={go}
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#009639] transition-colors px-2 py-1 rounded hover:bg-gray-100"
      data-testid="copy-btn"
    >
      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
    </button>
  );
}

function ChecklistItem({ item }) {
  const [checked, setChecked] = useState(false);
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${checked ? 'bg-green-50' : 'hover:bg-gray-50'}`}
      onClick={() => setChecked(!checked)}
      data-testid={`checklist-item-${item.step}`}
    >
      <div className="mt-0.5">
        {checked
          ? <CheckSquare className="w-5 h-5 text-[#009639]" />
          : <Square className="w-5 h-5 text-gray-300" />
        }
      </div>
      <div>
        <p className={`text-sm font-medium ${checked ? 'line-through text-gray-400' : 'text-gray-900'}`}>
          {item.step}. {item.title}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
      </div>
    </div>
  );
}

export default function JobApplyKitPage() {
  const { user } = useContext(AuthContext);

  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Form
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Result
  const [kit, setKit] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeSection, setActiveSection] = useState('cover_letters');

  useEffect(() => {
    checkAccess();
    fetchHistory();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API}/job-apply-kit/access/check`);
      setHasAccess(res.data.has_access);
    } catch {
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/job-apply-kit/history`);
      setHistory(res.data);
    } catch { /* ignore */ }
  };

  const handleGenerate = async () => {
    if (!jobTitle.trim()) { toast.error('Please enter a job title'); return; }
    if (!companyName.trim()) { toast.error('Please enter a company name'); return; }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/job-apply-kit/generate`, {
        job_title: jobTitle.trim(),
        company_name: companyName.trim()
      });
      setKit(res.data.kit);
      setActiveSection('cover_letters');
      toast.success('Job Apply Kit generated!');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate kit');
    } finally {
      setGenerating(false);
    }
  };

  const loadKit = (k) => {
    setKit(k);
    setActiveSection('cover_letters');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" />
      </div>
    );
  }

  // Paywall
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 max-w-2xl text-center">
          <Card className="p-10">
            <Lock className="w-16 h-16 text-gray-300 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-3">Job Apply Kit</h1>
            <p className="text-gray-600 mb-3 max-w-md mx-auto">
              Get AI-generated cover letters, professional email &amp; WhatsApp templates, and a step-by-step UAE job application checklist.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-500 mb-8">
              <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> 3 Cover Letters</span>
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email Template</span>
              <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> WhatsApp Template</span>
              <span className="flex items-center gap-1"><CheckSquare className="w-3.5 h-3.5" /> Apply Checklist</span>
            </div>
            <div className="space-y-3">
              <Link to="/pricing">
                <Button className="btn-uae-red px-8 py-3 text-base" data-testid="unlock-kit-btn">
                  Unlock Job Apply Kit — AED 199
                </Button>
              </Link>
              <p className="text-sm text-gray-500">
                Also included in the <strong>Pro plan</strong> (AED 49/month).
              </p>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const sections = [
    { key: 'cover_letters', label: 'Cover Letters', icon: FileText },
    { key: 'email', label: 'Email Template', icon: Mail },
    { key: 'whatsapp', label: 'WhatsApp Template', icon: MessageSquare },
    { key: 'checklist', label: 'Apply Checklist', icon: CheckSquare },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <PackageBanner />
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Link to="/dashboard" className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold" data-testid="job-apply-kit-title">Job Apply Kit</h1>
          </div>
          <p className="text-gray-600 ml-7">Generate a complete job application toolkit for any UAE role</p>
        </div>

        {/* Quick Access to Other Benefits */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Link to="/cv-checker" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer border-[#CE1126] border-opacity-30">
              <FileText className="w-5 h-5 text-[#CE1126] mx-auto mb-1" />
              <p className="text-xs font-medium">ATS CV Fix</p>
            </Card>
          </Link>
          <Link to="/interview-pack" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer border-amber-300 border-opacity-50">
              <MessageSquare className="w-5 h-5 text-amber-600 mx-auto mb-1" />
              <p className="text-xs font-medium">Interview Prep</p>
            </Card>
          </Link>
          <Link to="/interview-test" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer border-[#009639] border-opacity-30">
              <CheckSquare className="w-5 h-5 text-[#009639] mx-auto mb-1" />
              <p className="text-xs font-medium">English Test</p>
            </Card>
          </Link>
          <Link to="/salary" className="block">
            <Card className="p-3 text-center hover:shadow-md transition-shadow cursor-pointer border-blue-300 border-opacity-50">
              <FileText className="w-5 h-5 text-blue-600 mx-auto mb-1" />
              <p className="text-xs font-medium">Salary Guide</p>
            </Card>
          </Link>
        </div>

        {/* Input form */}
        {!kit && (
          <Card className="p-6 mb-8" data-testid="kit-form">
            <h2 className="text-lg font-semibold mb-5">Target position</h2>
            <div className="grid sm:grid-cols-2 gap-5 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Job Title *</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={e => setJobTitle(e.target.value)}
                  placeholder="e.g. Sales Executive, Admin Assistant..."
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                  data-testid="job-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g. Emirates NBD, Carrefour..."
                  className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
                  data-testid="company-name-input"
                />
              </div>
            </div>
            {user?.current_cv_content && (
              <p className="text-xs text-[#009639] mb-4 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Your uploaded CV will be used to personalise the content.
              </p>
            )}
            <Button
              className="btn-uae-red px-8"
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-kit-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Kit...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Job Apply Kit</>
              )}
            </Button>
          </Card>
        )}

        {/* Kit result */}
        {kit && (
          <>
            {/* Info bar */}
            <Card className="p-4 mb-6 flex flex-wrap items-center justify-between gap-3 bg-gray-900 text-white">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#CE1126]" />
                <span className="font-semibold">{kit.job_title}</span>
                <span className="text-gray-400">at</span>
                <span className="text-sm text-gray-300 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" /> {kit.company_name}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-white text-white hover:bg-white hover:text-gray-900"
                onClick={() => setKit(null)}
                data-testid="new-kit-btn"
              >
                New Kit
              </Button>
            </Card>

            {/* Sections nav */}
            <div className="flex border-b mb-6 gap-1 overflow-x-auto">
              {sections.map(s => (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeSection === s.key
                      ? 'border-[#CE1126] text-[#CE1126]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`tab-${s.key}`}
                >
                  <s.icon className="w-4 h-4" />
                  {s.label}
                </button>
              ))}
            </div>

            {/* Cover Letters */}
            {activeSection === 'cover_letters' && (
              <div className="space-y-5" data-testid="cover-letters-section">
                {(kit.cover_letters || []).map((cl, i) => (
                  <Card key={i} className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          i === 0 ? 'text-blue-600' : i === 1 ? 'text-purple-600' : 'text-green-600'
                        }`}>{cl.style}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{cl.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <CopyBtn text={cl.content} />
                        <Button
                          variant="outline" size="sm"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/job-apply-kit/${kit.id}/cover-letter-pdf/${i}`, { responseType: 'blob' });
                              const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const a = document.createElement('a'); a.href = url; a.download = `${cl.style.replace(/\s/g, '_')}_${kit.company_name || 'CL'}.pdf`; a.click();
                              toast.success('Cover letter PDF downloaded');
                            } catch { toast.error('Download failed'); }
                          }}
                          data-testid={`cl-pdf-${i}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> PDF
                        </Button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{cl.content}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Email Template */}
            {activeSection === 'email' && (
              <div data-testid="email-section">
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Professional Application Email</h3>
                  </div>
                  {kit.email_template && (
                    <>
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Subject Line</span>
                          <CopyBtn text={kit.email_template.subject} />
                        </div>
                        <p className="text-sm text-gray-800 font-medium">{kit.email_template.subject}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Body</span>
                          <CopyBtn text={kit.email_template.body} />
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{kit.email_template.body}</p>
                      </div>
                    </>
                  )}
                </Card>
              </div>
            )}

            {/* WhatsApp Template */}
            {activeSection === 'whatsapp' && (
              <div data-testid="whatsapp-section">
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">HR WhatsApp Message</h3>
                  </div>
                  {kit.whatsapp_template && (
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-[#009639] uppercase tracking-wide">Ready to Send</span>
                        <CopyBtn text={kit.whatsapp_template.message} />
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{kit.whatsapp_template.message}</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Copy and paste directly into WhatsApp to reach recruiters.
                  </p>
                </Card>
              </div>
            )}

            {/* Apply Checklist */}
            {activeSection === 'checklist' && (
              <div data-testid="checklist-section">
                <Card className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">UAE Job Application Checklist</h3>
                  <p className="text-xs text-gray-500 mb-4">Click items to mark them as done. This checklist resets on page reload.</p>
                  <div className="space-y-1">
                    {(kit.apply_checklist || []).map((item, i) => (
                      <ChecklistItem key={i} item={item} />
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </>
        )}

        {/* History */}
        {history.length > 0 && !kit && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-4">Previous Kits</h2>
            <div className="space-y-3" data-testid="kit-history">
              {history.map(h => (
                <Card
                  key={h.id}
                  className="p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => loadKit(h)}
                >
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-[#CE1126]" />
                    <div>
                      <p className="font-medium text-sm">{h.job_title} at {h.company_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(h.created_at).toLocaleDateString()}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
